/**
 * 資産台帳（Excel）画面のセル色・コメント情報の管理ユーティリティ
 *
 * 各資産（行）は cellMeta という JSON 文字列カラムを持ち、以下の形式でセル単位の
 * 背景色・コメントを保持する:
 *
 *   { "<元の列インデックス>": { "color"?: string, "comment"?: string } }
 *
 * 列インデックスは COLUMNS 配列（資産台帳（Excel）画面）の固定順を指す。
 * 表示上の列並び替え（columnOrder）とは独立して保存されるため、
 * ユーザーが列順を変更してもセルの色・コメントは正しい項目に紐付いたまま残る。
 */

export interface CellMetaEntry {
  color?: string;
  comment?: string;
}

export type CellMetaMap = Record<number, CellMetaEntry>;

/** 選択可能な背景色パレット（右クリックメニューの「セル色変更」用） */
export const CELL_COLOR_PALETTE: { label: string; value: string }[] = [
  { label: '赤', value: '#ffcdd2' },
  { label: '黄', value: '#fff9c4' },
  { label: '緑', value: '#c8e6c9' },
  { label: '青', value: '#bbdefb' },
  { label: 'グレー', value: '#e0e0e0' },
  { label: 'オレンジ', value: '#ffe0b2' },
  { label: '紫', value: '#e1bee7' },
  { label: '水色', value: '#b2ebf2' },
  { label: 'ピンク', value: '#f8bbd0' },
  { label: '黄緑', value: '#dcedc8' },
];

export function parseCellMeta(raw: string | null | undefined): CellMetaMap {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as CellMetaMap;
    }
  } catch {
    // ignore malformed JSON, treat as empty
  }
  return {};
}

export function serializeCellMeta(meta: CellMetaMap): string | null {
  const cleaned: CellMetaMap = {};
  for (const [key, entry] of Object.entries(meta)) {
    if (entry && (entry.color || entry.comment)) {
      cleaned[Number(key)] = entry;
    }
  }
  return Object.keys(cleaned).length > 0 ? JSON.stringify(cleaned) : null;
}

export function setCellColor(meta: CellMetaMap, colIdx: number, color: string | null): CellMetaMap {
  const next = { ...meta };
  const entry = { ...(next[colIdx] || {}) };
  if (color) {
    entry.color = color;
  } else {
    delete entry.color;
  }
  if (entry.color || entry.comment) {
    next[colIdx] = entry;
  } else {
    delete next[colIdx];
  }
  return next;
}

export function setCellComment(meta: CellMetaMap, colIdx: number, comment: string | null): CellMetaMap {
  const next = { ...meta };
  const entry = { ...(next[colIdx] || {}) };
  if (comment && comment.trim()) {
    entry.comment = comment.trim();
  } else {
    delete entry.comment;
  }
  if (entry.color || entry.comment) {
    next[colIdx] = entry;
  } else {
    delete next[colIdx];
  }
  return next;
}

// ============================================================
// Spreadsheet cell-name <-> coordinate helpers
//
// Jspreadsheet CE (jsuites) internally represents cells using spreadsheet-style
// names such as "A1", "B12", etc. Rather than depending on the library's
// undocumented static helpers (jexcel.getColumnNameFromId / getIdFromColumnName),
// we reimplement the same well-defined algorithm here so this logic is
// self-contained and doesn't break if the library's internals change.
// ============================================================

/** 0-indexed column + 0-indexed row -> spreadsheet cell name (e.g. [0,0] -> "A1"). */
export function cellNameFromCoords(colIdx: number, rowIdx: number): string {
  let col = colIdx;
  let letters = '';
  if (col > 701) {
    letters += String.fromCharCode(64 + Math.floor(col / 676));
    letters += String.fromCharCode(64 + Math.floor((col % 676) / 26));
  } else if (col > 25) {
    letters += String.fromCharCode(64 + Math.floor(col / 26));
  }
  letters += String.fromCharCode(65 + (col % 26));
  return `${letters}${rowIdx + 1}`;
}

/** Spreadsheet cell name (e.g. "B12") -> 0-indexed [col, row]. Returns [0,0] if unparsable. */
export function coordsFromCellName(cellName: string): [number, number] {
  const match = /^([A-Za-z]+)(\d+)$/.exec(cellName);
  if (!match) return [0, 0];
  const letters = match[1].toUpperCase();
  let col = 0;
  for (let i = 0; i < letters.length; i++) {
    col += (letters.charCodeAt(i) - 64) * Math.pow(26, letters.length - 1 - i);
  }
  col -= 1;
  if (col < 0) col = 0;
  const row = Math.max(0, parseInt(match[2], 10) - 1);
  return [col, row];
}

/** Extracts the `background-color` value from a CSS style string like "background-color:#fff;font-weight:bold". */
export function extractBackgroundColor(styleStr: string | null | undefined): string | null {
  if (!styleStr) return null;
  const match = /background-color\s*:\s*([^;]+)/i.exec(styleStr);
  return match ? match[1].trim() : null;
}
